import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import { useAuthStore } from '@/state/store/auth';
import { getGuestBlocksTokenSync } from '@/state/store/guestSession';
import { getEmbedAccessToken } from '@/modules/contract-checker-embed/embed-session';

const BLOCKS_AI_BASE = `${(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')}/blocksai-api/v1/ai-agent/chat`;

// Resolve a Bearer token: prefer the in-memory embed token (Contract Checker
// inside SELISE Signature), then the authenticated user's JWT, then the guest
// Blocks token. Returns null if none is provisioned.
function resolveBearer(): string | null {
  const embedToken = getEmbedAccessToken();
  if (embedToken) return embedToken;
  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated) return getGuestBlocksTokenSync();
  return null;
}

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

class FatalSSEError extends Error {}
export interface BlocksAgentResponse {
  message: string;
  messageId?: string;
  sessionId?: string;
  nextStepQuestions?: string[];
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryable(error: unknown): boolean {
  const s = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return (
    s.includes('timeout') ||
    s.includes('timed out') ||
    s.includes('rate limit') ||
    s.includes('429') ||
    s.includes('network') ||
    s.includes('fetch') ||
    s.includes('failed') ||
    s.includes('empty') ||
    s.includes('no content')
  );
}

// ---------------------------------------------------------------------------
// Core SSE call — resolves with the `chat_response` message payload
// ---------------------------------------------------------------------------

// Event names that may carry the agent's final answer. Different agents
// emit different events:
//   - Some emit `chat_response` (the original contract).
//   - Workflow agents emit `task_complete` with a possibly-truncated
//     preview, then a final payload on `workflow_end` or — confusingly —
//     `event: error` (some workflow runtimes mark the final delivery as
//     an "error" event even though it's the successful result).
// We listen to all of them but apply EVENT_PRIORITY: chat_response is
// the canonical final payload and always wins over a task_complete
// preview, regardless of which has more characters. Length is only
// used to break ties within the same priority tier (e.g. two
// task_complete events).
const FINAL_PAYLOAD_EVENTS = new Set([
  'chat_response',
  'task_complete',
  'workflow_end',
  'error',
]);

const EVENT_PRIORITY: Record<string, number> = {
  chat_response: 4,
  workflow_end: 3,
  task_complete: 2,
  error: 1,
};

const ENVELOPE_KEYS = ['result', 'data', 'answer', 'response'] as const;

/**
 * Strip a single-key `{result: {...}}` (or `{data: ...}`, `{answer: ...}`,
 * `{response: ...}`) envelope. Used both for object payloads (before
 * stringification) and as a defensive sweep when downstream parses fail.
 */
function unwrapEnvelopeObject(obj: Record<string, unknown>): Record<string, unknown> {
  for (const key of ENVELOPE_KEYS) {
    const inner = obj[key];
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner as Record<string, unknown>;
    }
  }
  return obj;
}

/**
 * Extract the actual reply text from one SSE event's parsed data.
 *
 * Payload shapes we've observed:
 *   { message: "..." }                             → plain string
 *   { message: { answer: "..." } }                 → wrapped (workflow agents)
 *   { message: { result: {...} } }                 → wrapped final payload
 *   { message: { answer: "...", ...meta } }        → wrapped + metadata
 *   { error: "...", message?: "..." }              → real error
 *
 * Returns null if no usable text is present (so the caller skips it
 * and keeps any larger payload seen on a prior event).
 */
function extractMessageText(parsed: Record<string, unknown>): string | null {
  const raw = parsed.message;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (typeof obj.answer === 'string') return obj.answer;
    // Unwrap a single `{result: {...}}` / `{data: ...}` envelope so
    // callers receive the inner payload directly. Idempotent if no
    // envelope is present.
    const unwrapped = unwrapEnvelopeObject(obj);
    return JSON.stringify(unwrapped);
  }
  return null;
}

async function callAgent(
  agentId: string,
  message: string,
  signal?: AbortSignal
): Promise<BlocksAgentResponse | null> {
  const accessToken = resolveBearer();
  const isAuthenticated = useAuthStore.getState().isAuthenticated;

  if (!accessToken && !isAuthenticated) throw new Error('No access token available (auth or guest)');

  let result: BlocksAgentResponse | null = null;
  let bestPriority = -1;
  let received = false;
  let agentError: string | null = null;

  await fetchEventSource(`${BLOCKS_AI_BASE}/${agentId}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      'x-blocks-key': import.meta.env.VITE_X_BLOCKS_KEY,
      Origin: 'https://api.seliseblocks.com/',
      Referer: 'https://api.seliseblocks.com/',
    },
    body: JSON.stringify({ message }),
    signal,
    openWhenHidden: true,

    async onopen(response) {
      if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType)) {
        return;
      }
      if (response.status === 401 || response.status === 403) {
        // An authenticated user's token is dead and could not be refreshed —
        // clear auth state so the app redirects to /login with the expired
        // banner. Guest sessions don't own a refresh token, so leave them.
        if (useAuthStore.getState().isAuthenticated) {
          useAuthStore.getState().expireSession();
        }
        throw new FatalSSEError(`Authentication failed (status ${response.status})`);
      }
      const text = await response.text().catch(() => '');
      throw new Error(`Blocks Agent request failed (status ${response.status}): ${text}`);
    },

    onmessage(event) {
      if (!event.data || !FINAL_PAYLOAD_EVENTS.has(event.event)) return;

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }

      const text = extractMessageText(parsed);

      // A true error: explicit `error` field AND no usable text. Some
      // workflows put the successful payload on `event: error`, so we
      // can't blanket-fail on the event name alone.
      if (typeof parsed.error === 'string' && !text) {
        agentError = parsed.error;
        return;
      }

      if (!text) return;

      // A structured object `message` is the agent's actual final answer:
      // workflow agents deliver the full JSON as an object (sometimes on
      // `event: error`), whereas string payloads may be a truncated preview
      // (task_complete) or a prose summary (workflow_end). So an object
      // payload outranks ANY string payload; the per-event priority only
      // orders payloads of the same structured-ness. Within one tier, keep
      // the largest payload (later task_complete previews are fuller).
      const isStructured = !!parsed.message && typeof parsed.message === 'object';
      const priority = (isStructured ? 100 : 0) + (EVENT_PRIORITY[event.event] ?? 0);
      const sameTierLonger =
        priority === bestPriority && result !== null && text.length > result.message.length;
      if (priority > bestPriority || sameTierLonger) {
        result = {
          message: text,
          messageId: parsed.messageId as string | undefined,
          sessionId: parsed.sessionId as string | undefined,
          nextStepQuestions: parsed.nextStepQuestions as string[] | undefined,
        };
        bestPriority = priority;
        received = true;
      }
    },

    onclose() {
      // stream ended
    },

    onerror(err) {
      throw err;
    },
  });

  if (!received) {
    if (agentError) throw new Error(`Blocks Agent error: ${agentError}`);
    throw new Error('Blocks Agent returned no final payload');
  }

  return result;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Send a message to a pre-configured Blocks AI Agent.
 *
 * Resolves with the `message` field from the `chat_response` SSE event
 * (string or parsed JSON object, depending on the agent configuration).
 */
export async function queryBlocksAgent(
  agentId: string,
  message: string,
  signal?: AbortSignal
): Promise<BlocksAgentResponse | null> {
  for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[BlocksAgent] Attempt ${attempt}/${MAX_RETRIES + 1} (agent: ${agentId})`);
    }

    try {
      return await callAgent(agentId, message, signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      if (error instanceof FatalSSEError) throw error;

      if (import.meta.env.DEV) {
        console.error(`[BlocksAgent] Attempt ${attempt} failed:`, error);
      }

      if (isRetryable(error) && attempt <= MAX_RETRIES) {
        await sleep(RETRY_DELAY_MS * Math.pow(2, attempt - 1));
        continue;
      }

      throw error;
    }
  }

  throw new Error('All retry attempts exhausted');
}
